import {
  type AmplifyError,
  updatePassword,
} from '@backoffice-frontend/authentication'
import { useForm } from '@backoffice-frontend/forms'
import { PenIcon } from '@moia-dev/pace-icons'
import { Button, Dialog, IconButton, useDialog } from '@moia-dev/pace-web'
import { useState } from 'react'
import { FormProvider } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import {
  ChangePasswordForm,
  type ChangePasswordValues,
} from './ChangePasswordForm'

export const ChangePasswordDialog = () => {
  const { t } = useTranslation()
  const { id: dialogId, openDialog, closeDialog, isOpen } = useDialog()

  const [changeSuccess, setChangeSuccess] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string>()

  const formId = 'change-password-form'

  const formMethods = useForm<ChangePasswordValues>({
    mode: 'onChange',
    defaultValues: {
      oldPassword: '',
      newPassword: '',
      newPasswordRepeat: '',
    },
  })

  const {
    formState: { isSubmitting, isValid },
  } = formMethods

  const handleSubmitPassword = async ({
    newPassword,
    oldPassword,
  }: ChangePasswordValues) => {
    try {
      await updatePassword({ oldPassword, newPassword })
      setChangeSuccess(true)

      // Close dialog after showing success message
      setTimeout(() => {
        closeDialog()
        setChangeSuccess(false)
        setErrorMessage(undefined)
        formMethods.reset()
      }, 2000)
    } catch (_error) {
      const error = _error as AmplifyError
      if (error.name === 'InvalidPasswordException') {
        setErrorMessage(t('common_passwordPolicy_error'))
      } else {
        setErrorMessage(t('authenticatedAppView_oldPasswordIncorrect'))
      }
    }
  }

  return (
    <div css={{ width: '100%' }}>
      {isOpen && (
        <Dialog
          id={dialogId}
          title={t('authenticatedAppView_managePassword_label')}
          footer={
            <div
              css={theme => ({
                display: 'flex',
                gap: theme.space.Space2,
                justifyContent: 'flex-end',
              })}
            >
              <Button
                label={t('common_cancel_button')}
                variant="secondary"
                size="compact"
                onClick={closeDialog}
              />
              <Button
                label={t('authenticatedAppView_changePassword_button')}
                size="compact"
                type="submit"
                form={formId}
                disabled={isSubmitting || !isValid}
              />
            </div>
          }
        >
          <FormProvider {...formMethods}>
            <ChangePasswordForm
              formId={formId}
              onSubmit={handleSubmitPassword}
              changeSuccess={changeSuccess}
              errorMessage={errorMessage}
            />
          </FormProvider>
        </Dialog>
      )}
      <IconButton
        label={t('authenticatedAppView_managePassword_label')}
        variant="plain"
        size="compact"
        icon={<PenIcon />}
        onClick={openDialog}
      />
    </div>
  )
}
